/**
 * AIAssistToggle — opt-in checkbox for the AI-assist enrichment overlay.
 *
 * Hard contract:
 *   - Default OFF. Operator must opt in per request.
 *   - When ON, an explicit "AI-assisted draft, still requires human
 *     review" banner is rendered next to the checkbox.
 *   - The deterministic engine remains the source of truth — this
 *     component never bypasses or hides the deterministic body.
 *
 * The component is locale-aware via the i18n strings dictionary; no
 * raw English strings appear in non-English locales.
 */

import type { JSX } from "react";

interface AIAssistToggleProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  hint: string;
  banner: string;
  disabled?: boolean;
  testId?: string;
}

export function AIAssistToggle({
  checked,
  onChange,
  label,
  hint,
  banner,
  disabled,
  testId,
}: AIAssistToggleProps): JSX.Element {
  return (
    <div
      className="po-ai-assist-toggle"
      data-testid={testId ?? "po-ai-assist-toggle"}
    >
      <label className="po-ai-assist-toggle__row">
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          data-testid={`${testId ?? "po-ai-assist-toggle"}-input`}
        />
        <span className="po-ai-assist-toggle__label">{label}</span>
      </label>
      <p className="po-note po-ai-assist-toggle__hint">{hint}</p>
      {checked ? (
        <div
          className="po-banner po-banner--warn"
          role="status"
          data-testid="po-ai-assist-banner"
        >
          {banner}
        </div>
      ) : null}
    </div>
  );
}

export default AIAssistToggle;
